<template>
  <div class="image-uploader-demo">
    <Sidebar />
    <div class="main-content">
      <div class="container py-4">
        
        <!-- Images Section -->
        <h3 class="mb-3">Image Uploaders</h3>
        <div class="row g-4">
          <div class="col-md-6">
            <div class="card">
              <div class="card-header">
                <h3>Basic Image Uploader</h3>
              </div>
              <div class="card-body">
                <ImageUploader 
                  v-model="basicImage"
                  title="Upload Pictures"
                  placeholder="Click here to upload your pictures"
                  @upload-success="handleImageUploadSuccess"
                  @upload-error="handleUploadError"
                  @image-removed="handleImageRemoved"
                />
                
                <div v-if="basicImage" class="mt-3">
                  <h5>Image URL:</h5>
                  <div class="image-url">{{ basicImage }}</div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="col-md-6">
            <div class="card">
              <div class="card-header">
                <h3>Custom Image Configuration</h3>
              </div>
              <div class="card-body">
                <ImageUploader 
                  v-model="customImage"
                  title="Upload Image"
                  placeholder="Upload a image (PNG, JPEG, GIF, WEBP)"
                  :maxFileCount="5"
                  @upload-success="handleCustomImageUploadSuccess"
                />
                
                <div v-if="customImage" class="mt-3">
                  <h5>Image URL:</h5>
                  <div class="image-url">{{ customImage }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Audio Section -->
        <h3 class="mb-3 mt-5">Audio Uploaders</h3>
        <div class="row g-4">
          <div class="col-md-6">
            <div class="card">
              <div class="card-header">
                <h3>Basic Audio Uploader</h3>
              </div>
              <div class="card-body">
                <AudioUploader 
                  v-model="basicAudio"
                  title="Upload Audio"
                  placeholder="Click here to upload your audio"
                  @upload-success="handleAudioUploadSuccess"
                  @upload-error="handleUploadError"
                  @audio-removed="handleAudioRemoved"
                />
                
                <div v-if="basicAudio" class="mt-3">
                  <h5>Audio URL:</h5>
                  <div class="image-url">{{ basicAudio }}</div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="col-md-6">
            <div class="card">
              <div class="card-header">
                <h3>Custom Audio Configuration</h3>
              </div>
              <div class="card-body">
                <AudioUploader 
                  v-model="customAudio"
                  title="Upload Audio File"
                  placeholder="Upload audio (MP3, WAV, OGG, etc.)"
                  :maxFileCount="3"
                  @upload-success="handleCustomAudioUploadSuccess"
                />
                
                <div v-if="customAudio" class="mt-3">
                  <h5>Audio URL:</h5>
                  <div class="image-url">{{ customAudio }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ImageUploader from './ImageUploader.vue';
import AudioUploader from './AudioUploader.vue';
import Sidebar from './Sidebar.vue';
import axios from 'axios';

export default {
  name: 'ImageUploaderDemo',
  components: {
    ImageUploader,
    AudioUploader,
    Sidebar
  },
  data() {
    return {
      // Image URLs
      basicImage: '',
      customImage: '',
      // Audio URLs
      basicAudio: '',
      customAudio: '',
      // Upload history
      uploadHistory: [],
      // Audio player
      audioPlayerVisible: false,
      currentAudioUrl: '',
      // Loading state
      isLoading: false,
      errorMessage: ''
    };
  },
  mounted() {
    // Fetch upload history from server when component is mounted
    this.fetchUploadHistory();
  },
  methods: {
    // Server communication methods
    async fetchUploadHistory() {
      this.isLoading = true;
      this.errorMessage = '';
      
      try {
        // Updated API endpoint with correct port
        const response = await axios.get('http://localhost:3001/api/uploads');
        this.uploadHistory = response.data.map(item => ({
          ...item,
          time: new Date(item.createdAt || Date.now()).toLocaleString()
        }));
      } catch (error) {
        console.error('Failed to fetch upload history:', error);
        this.errorMessage = 'Failed to load upload history from server';
        // Fallback to empty array for error handling
        this.uploadHistory = [];
      } finally {
        this.isLoading = false;
      }
    },
    
    async saveUploadToServer(uploadData) {
      try {
        // Updated API endpoint with correct port
        await axios.post('http://localhost:3001/api/uploads', uploadData);
        console.log('Upload saved to server successfully');
      } catch (error) {
        console.error('Failed to save upload to server:', error);
        // Even if server save fails, we still show it in local history
      }
    },
    
    async deleteUploadFromServer(id) {
      try {
        // Updated API endpoint with correct port
        await axios.delete(`http://localhost:3001/api/uploads/${id}`);
        console.log('Upload deleted from server successfully');
        return true;
      } catch (error) {
        console.error('Failed to delete upload from server:', error);
        return false;
      }
    },

    // Image upload handlers
    async handleImageUploadSuccess(url) {
      console.log('Basic image uploaded successfully:', url);
      const uploadData = {
        url: url,
        createdAt: new Date().toISOString(),
        type: 'Basic Image Upload',
        fileType: 'image'
      };
      
      // Add to local state
      this.addToHistory(uploadData);
      
      // Save to server
      await this.saveUploadToServer(uploadData);
    },
    
    async handleCustomImageUploadSuccess(url) {
      console.log('Custom image uploaded successfully:', url);
      const uploadData = {
        url: url,
        createdAt: new Date().toISOString(),
        type: 'Custom Image Upload',
        fileType: 'image'
      };
      
      // Add to local state
      this.addToHistory(uploadData);
      
      // Save to server
      await this.saveUploadToServer(uploadData);
    },
    
    handleImageRemoved() {
      console.log('Image removed');
    },
    
    // Audio upload handlers
    async handleAudioUploadSuccess(url, fileName) {
      console.log('Basic audio uploaded successfully:', url);
      const uploadData = {
        url: url,
        createdAt: new Date().toISOString(),
        type: 'Basic Audio Upload',
        fileType: 'audio',
        fileName: fileName
      };
      
      // Add to local state
      this.addToHistory(uploadData);
      
      // Save to server
      await this.saveUploadToServer(uploadData);
    },
    
    async handleCustomAudioUploadSuccess(url, fileName) {
      console.log('Custom audio uploaded successfully:', url);
      const uploadData = {
        url: url,
        createdAt: new Date().toISOString(),
        type: 'Custom Audio Upload',
        fileType: 'audio',
        fileName: fileName
      };
      
      // Add to local state
      this.addToHistory(uploadData);
      
      // Save to server
      await this.saveUploadToServer(uploadData);
    },
    
    handleAudioRemoved() {
      console.log('Audio removed');
    },
    
    // Common error handler
    handleUploadError(error) {
      console.error('Upload failed:', error);
    },
    
    // History management
    addToHistory(uploadData) {
      // Format the time for display
      const displayData = {
        ...uploadData,
        time: new Date(uploadData.createdAt).toLocaleString()
      };
      
      this.uploadHistory.unshift(displayData);
    },
    
    async deleteHistoryItem(index) {
      const item = this.uploadHistory[index];
      
      // If the item has an ID (from server), delete it from server
      if (item.id) {
        const success = await this.deleteUploadFromServer(item.id);
        if (!success) {
          alert('Failed to delete from server. Try again later.');
          return;
        }
      }
      
      // Remove from local state
      this.uploadHistory.splice(index, 1);
    },
    
    async clearAllHistory() {
      if (confirm('Are you sure you want to clear all upload history? This will delete all uploads from the server.')) {
        this.isLoading = true;
        
        try {
          // Updated API endpoint with correct port
          await axios.delete('http://localhost:3001/api/uploads');
          
          // Clear local state
          this.uploadHistory = [];
          console.log('All uploads deleted successfully');
        } catch (error) {
          console.error('Failed to clear all uploads:', error);
          alert('Failed to clear all uploads from server. Try again later.');
        } finally {
          this.isLoading = false;
        }
      }
    },
    
    // Audio player functionality
    playAudio(url) {
      this.currentAudioUrl = url;
      this.audioPlayerVisible = true;
    },
    
    closeAudioPlayer() {
      this.audioPlayerVisible = false;
      this.currentAudioUrl = '';
    },
    
    // Refresh history from server
    refreshHistory() {
      this.fetchUploadHistory();
    }
  }
};
</script>

<style scoped>
.image-uploader-demo {
  display: flex;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  margin-left: 300px; /* Adjust based on your sidebar width */
  padding-bottom: 2rem;
}

.card {
  margin-bottom: 1.5rem;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border: none;
}

.card-header {
  background-color: #f8f9fa;
  padding: 1rem;
  border-bottom: 1px solid #eee;
}

.card-header h3 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.card-body {
  padding: 1.5rem;
  position: relative;
}

.image-url {
  padding: 0.75rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  font-family: monospace;
  word-break: break-all;
  font-size: 0.875rem;
}

.upload-thumbnail {
  width: 60px;
  height: 60px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
}

.upload-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.audio-thumbnail {
  background-color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
}

.audio-thumbnail svg {
  color: white;
}

.upload-details {
  flex-grow: 1;
  font-size: 0.875rem;
}

.action-buttons {
  display: flex;
  align-items: center;
}

.delete-btn, 
.btn-primary {
  width: 36px;
  height: 36px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.delete-btn svg,
.btn-primary svg {
  color: white;
}

/* Audio player modal */
.audio-player-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.audio-player-container {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  padding: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
}

.audio-player-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.audio-player-header h4 {
  margin: 0;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #6c757d;
}

.close-btn:hover {
  color: #343a40;
}

.modal-audio-player {
  width: 100%;
}

/* Spinner and loading styles */
.spinner-border {
  width: 3rem;
  height: 3rem;
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 60px; /* For mobile sidebar height */
  }
}
</style> 